import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { InboxLayout } from "./useInboxLayout";

interface InboxSplitViewProps {
  layout: InboxLayout;
  listAriaLabel: string;
  detailAriaLabel: string;
  /** Content of the sticky toolbar block above the list. */
  toolbar: ReactNode;
  /** Error / skeleton / empty state / list of cards (`[data-order-card]`). */
  list: ReactNode;
  /** Usually an <InboxPager/>. */
  footer?: ReactNode;
  /** Drives aria-busy on the scroll list. */
  isFetching: boolean;
  /** The list dims while refetching, but not on the first load. */
  isLoading: boolean;
  /** Single-pane push flow: the detail pane is open over the list. */
  detailOpen: boolean;
  /** `visible` = the detail pane is on screen (two-pane, or opened). */
  renderDetail: (args: { twoPane: boolean; visible: boolean }) => ReactNode;
}

/**
 * Two-pane inbox shell (approved Layout C), shared by the client Orders inbox
 * and the owner Client messages inbox. Left: a 460px list pane with its own
 * toolbar and scroll. Right: the detail pane. The page itself never scrolls;
 * each pane scrolls independently inside the viewport remainder under the
 * TopHeader. Below 820px of available width the panes swap to a single-pane
 * push flow (list → detail with a Back button).
 */
export function InboxSplitView({
  layout,
  listAriaLabel,
  detailAriaLabel,
  toolbar,
  list,
  footer,
  isFetching,
  isLoading,
  detailOpen,
  renderDetail,
}: InboxSplitViewProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLElement>(null);
  const { twoPane } = layout;
  const visible = twoPane || detailOpen;

  // Single-pane push flow focus: opening the detail moves focus to its heading
  // (the list it came from is now hidden); Back returns focus to the selected
  // card and scrolls it into view. Transitions only, never on first render.
  const prevDetailOpen = useRef(detailOpen);
  useEffect(() => {
    const wasOpen = prevDetailOpen.current;
    prevDetailOpen.current = detailOpen;
    if (twoPane || wasOpen === detailOpen) return;
    if (detailOpen) {
      const target =
        detailRef.current?.querySelector<HTMLElement>("h2[tabindex]") ??
        detailRef.current;
      target?.focus();
      return;
    }
    const card = listRef.current?.querySelector<HTMLElement>(
      '[data-order-card][aria-current="true"]',
    );
    if (card) {
      card.focus();
      card.scrollIntoView?.({ block: "nearest" });
    }
  }, [detailOpen, twoPane]);

  // Arrow-key navigation over the card list: move focus AND selection so the
  // right pane follows the keyboard (Enter/Space already work — cards are
  // buttons).
  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const cards = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        "[data-order-card]:not(:disabled)",
      ) ?? [],
    );
    if (cards.length === 0) return;
    const activeIndex = cards.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    const nextIndex =
      event.key === "ArrowDown"
        ? Math.min(cards.length - 1, activeIndex + 1)
        : Math.max(0, activeIndex === -1 ? 0 : activeIndex - 1);
    const next = cards[nextIndex];
    if (!next) return;
    event.preventDefault();
    next.focus();
    next.click();
  };

  return (
    // The inbox occupies exactly the viewport remainder under the TopHeader and
    // clips: the ONLY scrollers inside are the card list and the chat.
    //
    // No `flex-1`/`min-h-0` here — `flex-1` means `flex-basis: 0%` + grow, which
    // hands the main-axis size back to the flex algorithm and overrides this
    // explicit height. AppLayout's <main> has `min-height: auto`, so a tall
    // child (e.g. the detail pane squeezed narrow) grew <main>, and this
    // container grew with it: the whole page scrolled instead of the list.
    //
    // `data-fills-viewport` lets a layout drop its bottom padding for this
    // full-height child (ProductionLayout's <main>). The version-skew banner and
    // DevTools panel (ProductionLayout only) publish their heights; both
    // default to 0px everywhere else.
    <div
      ref={layout.ref}
      data-fills-viewport
      className="flex h-[calc(100dvh-var(--top-header-height)-var(--version-banner-height,0px)-var(--devtools-bar-height,0px))] overflow-hidden"
    >
      {/* LEFT — list pane */}
      <section
        aria-label={listAriaLabel}
        className={cn(
          "flex min-h-0 flex-col bg-card",
          twoPane ? "w-[460px] shrink-0 border-r" : "w-full",
          twoPane ? "flex" : detailOpen && "hidden",
        )}
      >
        {/* Toolbar — sticky by construction (the scroll region is below it) */}
        <div className="shrink-0 space-y-2.5 border-b px-3.5 py-3">
          {toolbar}
        </div>

        {/* Scrollable card list */}
        <div
          ref={listRef}
          onKeyDown={handleListKeyDown}
          className={cn(
            "min-h-0 flex-1 overflow-y-auto transition-opacity duration-200 ease-out motion-reduce:transition-none",
            isFetching && !isLoading && "opacity-60",
          )}
          aria-busy={isFetching}
        >
          {list}
        </div>

        {footer}
      </section>

      {/* RIGHT — detail pane */}
      <section
        ref={detailRef}
        tabIndex={-1}
        aria-label={detailAriaLabel}
        className={cn(
          "min-w-0 flex-1 flex-col bg-background focus:outline-none",
          visible ? "flex" : "hidden",
        )}
      >
        {renderDetail({ twoPane, visible })}
      </section>
    </div>
  );
}

interface InboxPagerProps {
  offset: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  disabled: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

/** Pagination footer for an InboxSplitView list; renders nothing on one page. */
export function InboxPager({
  offset,
  pageSize,
  total,
  hasMore,
  disabled,
  onPrevious,
  onNext,
}: InboxPagerProps) {
  const { t } = useTranslation("clientOrders");
  if (total <= pageSize) return null;
  const totalPages = Math.ceil(total / pageSize);
  const currentPage = Math.floor(offset / pageSize) + 1;

  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-t px-3.5 py-2">
      <span className="text-xs text-muted-foreground">
        {t("pagination.page", { current: currentPage, total: totalPages })}
      </span>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          disabled={offset <= 0 || disabled}
          onClick={onPrevious}
        >
          {t("pagination.previous")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          disabled={!hasMore || disabled}
          onClick={onNext}
        >
          {t("pagination.next")}
        </Button>
      </div>
    </div>
  );
}
